/**
 * <sticky-atc> — Smart-Sticky Add-to-Cart Bar für Mobile (Sprint 018k Option 2).
 *
 * Verhalten:
 *  - Sichtbar wenn Original-ATC out-of-viewport.
 *  - Preis wird live vom Original gemirrort.
 *  - Text wird kontextabhängig gesetzt:
 *      · Original-ATC enabled  → Text spiegelt Original ("In den Warenkorb")
 *      · Original-ATC disabled + Mix&Match unvollständig → data-invalid-text ("Duftrichtungen wählen")
 *      · Original-ATC disabled + Variant sold out (kein Picker) → Original-Text (i.d.R. "In den Warenkorb")
 *  - Klick-Verhalten:
 *      · enabled  → direktes originalAtc.click() (spart Klick + Scroll)
 *      · disabled → smooth Scroll zum Mix&Match-Picker (oder ATC als Fallback)
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.priceMirror   = this.querySelector('[data-sticky-price]');
    this.compareMirror = this.querySelector('[data-sticky-compare]');
    this.savingsMirror = this.querySelector('[data-sticky-savings]');
    this.button        = this.querySelector('[data-sticky-button]');
    this.textEl        = this.querySelector('[data-sticky-text]');
    this.invalidText   = this.dataset.invalidText || '';

    this.originalAtc     = document.querySelector('.product__add-to-cart');
    this.originalPrice   = document.querySelector('[data-final-price]');
    this.originalCompare = document.querySelector('[data-final-compare]');
    this.originalSavings = document.querySelector('[data-savings]');
    this.mixMatchPicker  = document.querySelector('mix-match-picker');

    if (!this.originalAtc) return;

    /* Original-Text merken als Fallback wenn textContent leer / während Loading */
    this.originalDefaultText = this.originalAtc.textContent.trim();

    this.setupVisibility();
    this.setupPriceSync();
    this.setupCompareSync();
    this.setupSavingsSync();
    this.setupStateSync();
    this.syncButtonState();

    this.button?.addEventListener('click', this.onClick.bind(this));

    /* Sprint 019d #16: Prefetch /cart bei Sticky-ATC Interaction.
       Conservative Trigger: erst wenn User Absicht signalisiert (touchstart/pointerenter).
       Nur EINMAL pro Session — kein wiederholtes Prefetching.
       Kein Prefetch wenn Original-ATC disabled (Configurator incomplete). */
    this._prefetched = false;
    const prefetch = () => {
      if (this._prefetched) return;
      if (this.originalAtc?.disabled) return;
      this._prefetched = true;
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = '/cart';
      link.as = 'document';
      document.head.appendChild(link);
    };
    /* Passive listener → INP-freundlich, kein preventDefault möglich (brauchen wir nicht) */
    this.button?.addEventListener('pointerenter', prefetch, { passive: true, once: true });
    this.button?.addEventListener('touchstart', prefetch, { passive: true, once: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.priceObserver?.disconnect();
    this.compareObserver?.disconnect();
    this.savingsObserver?.disconnect();
    this.textObserver?.disconnect();
    this.disabledObserver?.disconnect();
    if (this.onMixMatchChange) {
      document.removeEventListener('mix-match:valid', this.onMixMatchChange);
      document.removeEventListener('mix-match:invalid', this.onMixMatchChange);
    }
  }

  /**
   * Visibility (Sprint 018l fix): nur sichtbar wenn Original-ATC OBERHALB
   * des Viewports ist (User bereits daran vorbeigescrollt). Solange der ATC
   * unten wartet oder im Viewport ist → Sticky bleibt versteckt.
   */
  setupVisibility() {
    this.observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      const isAbove = entry.boundingClientRect.top < 0;
      const scrolledPast = !entry.isIntersecting && isAbove;
      this.classList.toggle('is-visible', scrolledPast);
    }, { threshold: 0, rootMargin: '0px 0px -80px 0px' });
    this.observer.observe(this.originalAtc);
  }

  setupPriceSync() {
    if (!this.priceMirror || !this.originalPrice) return;
    this.priceMirror.textContent = this.originalPrice.textContent;

    this.priceObserver = new MutationObserver(() => {
      this.priceMirror.textContent = this.originalPrice.textContent;
    });
    this.priceObserver.observe(this.originalPrice, {
      childList: true, characterData: true, subtree: true,
    });
  }

  setupCompareSync() {
    if (!this.compareMirror || !this.originalCompare) return;
    this.syncCompare();

    /* Watch content + hidden-attribute changes */
    this.compareObserver = new MutationObserver(() => this.syncCompare());
    this.compareObserver.observe(this.originalCompare, {
      childList: true, characterData: true, subtree: true,
      attributes: true, attributeFilter: ['hidden'],
    });
  }

  syncCompare() {
    const txt = this.originalCompare.textContent.trim();
    const isHidden = this.originalCompare.hidden || !txt;
    this.compareMirror.hidden = isHidden;
    if (!isHidden) this.compareMirror.textContent = txt;
  }

  setupSavingsSync() {
    if (!this.savingsMirror || !this.originalSavings) return;
    this.syncSavings();

    /* Watch textContent + class (product__price-savings--hidden toggle) */
    this.savingsObserver = new MutationObserver(() => this.syncSavings());
    this.savingsObserver.observe(this.originalSavings, {
      childList: true, characterData: true, subtree: true,
      attributes: true, attributeFilter: ['class', 'hidden'],
    });
  }

  syncSavings() {
    const txt = this.originalSavings.textContent.trim();
    const isHiddenByClass = this.originalSavings.classList.contains('product__price-savings--hidden');
    const isHidden = this.originalSavings.hidden || isHiddenByClass || !txt;
    this.savingsMirror.hidden = isHidden;
    if (!isHidden) this.savingsMirror.textContent = txt;
  }

  setupStateSync() {
    /* Original-Text-Änderungen (z.B. "…" beim Loading) triggern State-Sync */
    this.textObserver = new MutationObserver(() => this.syncButtonState());
    this.textObserver.observe(this.originalAtc, {
      childList: true, characterData: true, subtree: true,
    });

    /* Disabled-Attribut-Änderungen (variant sold out / mix-match invalid) */
    this.disabledObserver = new MutationObserver(() => this.syncButtonState());
    this.disabledObserver.observe(this.originalAtc, {
      attributes: true, attributeFilter: ['disabled'],
    });

    /* Mix&Match Events (bubbling — landen am document) */
    this.onMixMatchChange = () => this.syncButtonState();
    document.addEventListener('mix-match:valid', this.onMixMatchChange);
    document.addEventListener('mix-match:invalid', this.onMixMatchChange);
  }

  syncButtonState() {
    if (!this.button) return;

    const isDisabled = this.originalAtc.disabled;
    const mixIncomplete = this.mixMatchPicker
      ? (typeof this.mixMatchPicker.isValid === 'function' && !this.mixMatchPicker.isValid())
      : false;

    /* Text-Copy */
    let label;
    if (isDisabled && mixIncomplete && this.invalidText) {
      label = this.invalidText;
    } else {
      label = this.originalAtc.textContent.trim() || this.originalDefaultText;
    }
    if (this.textEl) {
      this.textEl.textContent = label;
    } else {
      this.button.textContent = label;
    }

    /* Visual "blocked"-State — Button bleibt klickbar (löst Scroll aus),
       aber Optik zeigt den Zustand. CSS kann per .is-blocked stylen. */
    this.classList.toggle('is-blocked', isDisabled);
  }

  onClick(e) {
    e.preventDefault();

    if (this.originalAtc.disabled) {
      /* Invalid → Scroll zum blockierenden Step */
      const target = this.mixMatchPicker || this.originalAtc;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('is-focused');
      setTimeout(() => target.classList.remove('is-focused'), 900);
    } else {
      /* Valid → direkter Klick auf Original-ATC (löst Submit + Cart-Add aus) */
      this.originalAtc.click();
    }
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
